import React, { useMemo, useState } from 'react'
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  CircularProgress,
  Stack,
  alpha,
  Typography
} from '@mui/material'
import { ISTableTitle, ISTableHeaderCell, ISTableRow } from '../../Utils/ISTable'
import { useTranslation } from 'react-i18next'
import { formattedCurrency } from '../../../utils/StringUtils'
import moment from 'moment'
import { CurrentAccountSummary } from '../../../models/CurrentAccountSummary'
import { useThemeContext } from '../../../theme/ThemeContext'

type Order = 'asc' | 'desc'

interface Props {
  rows: CurrentAccountSummary[]
  loading: boolean
  isAccounts?: boolean
  onClickDetail: (row: CurrentAccountSummary) => void
}

const CurrentAccountSummaryTable: React.FC<Props> = ({ rows, loading, onClickDetail, isAccounts }) => {
  const { t } = useTranslation(['current', 'goal', 'client'])
  const { theme, isSmallScreen } = useThemeContext()

  const [orderBy, setOrderBy] = useState<keyof CurrentAccountSummary>('codigo_cliente')
  const [orderDirection, setOrderDirection] = useState<Order>('asc')

  const calculateTotals = (data: CurrentAccountSummary[]) => {
    return data.reduce(
      (acc, row) => {
        acc.total_documentos_vencidos += row.total_documentos_vencidos || 0
        acc.total_rc_sin_aplicar += row.total_rc_sin_aplicar || 0
        acc.total_nc_sin_aplicar += row.total_nc_sin_aplicar || 0
        acc.saldo_cc += row.saldo_cc || 0
        acc.saldo_cc_ctrl_date += row.saldo_cc_ctrl_date || 0
        acc.total_cr += row.total_cr || 0
        acc.total_cr_ctrl_date += row.total_cr_ctrl_date || 0
        acc.total_ci_sin_recibo += row.total_ci_sin_recibo || 0
        return acc
      },
      {
        total_documentos_vencidos: 0,
        total_rc_sin_aplicar: 0,
        total_nc_sin_aplicar: 0,
        saldo_cc: 0,
        saldo_cc_ctrl_date: 0,
        total_cr: 0,
        total_cr_ctrl_date: 0,
        total_ci_sin_recibo: 0
      }
    )
  }

  const handleSort = (property: keyof CurrentAccountSummary) => {
    const isAsc = orderBy === property && orderDirection === 'asc'
    setOrderBy(property)
    setOrderDirection(isAsc ? 'desc' : 'asc')
  }

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      let valueA = a[orderBy]
      let valueB = b[orderBy]

      if (orderBy === 'codigo_cliente') {
        valueA = Number(valueA)
        valueB = Number(valueB)
      }

      if (valueA == null) return 1
      if (valueB == null) return -1

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return orderDirection === 'asc' ? valueA - valueB : valueB - valueA
      }

      return orderDirection === 'asc'
        ? String(valueA).localeCompare(String(valueB))
        : String(valueB).localeCompare(String(valueA))
    })
  }, [rows, orderBy, orderDirection])

  const nationalRows = useMemo(
    () => sortedRows.filter(row => row.market === 'NATIONAL'),
    [sortedRows]
  )

  const exportRows = useMemo(
    () => sortedRows.filter(row => row.market === 'EXPORT'),
    [sortedRows]
  )

  const nationalTotals = useMemo(
    () => calculateTotals(nationalRows),
    [nationalRows]
  )

  const exportTotals = useMemo(
    () => calculateTotals(exportRows),
    [exportRows]
  )

  const totals = useMemo(
    () => calculateTotals(rows),
    [rows]
  )

  const columnCount = isAccounts ? 13 : 11

  const renderRows = (data: CurrentAccountSummary[]) =>
    data.map((row, i) => (
      <ISTableRow
        key={`${row.market}-${row.codigo_cliente}-${i}`}
        color="default"
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08)
          }
        }}
        onClick={e => {
          e.stopPropagation()
          onClickDetail(row)
        }}
      >
        <TableCell>{row.zona_nombre}</TableCell>
        <TableCell>{row.vendedor}</TableCell>
        <TableCell align="right">{row.codigo_cliente}</TableCell>
        <TableCell>{row.cliente}</TableCell>
        <TableCell align="right">{formattedCurrency(row.total_documentos_vencidos)}</TableCell>

        <TableCell align="center">
          {row.vencimiento_mas_atrasado
            ? moment(row.vencimiento_mas_atrasado).format('DD/MM/YYYY')
            : '-'}
        </TableCell>

        <TableCell align="right">{formattedCurrency(row.total_rc_sin_aplicar)}</TableCell>
        <TableCell align="right">{formattedCurrency(row.total_nc_sin_aplicar)}</TableCell>
        <TableCell align="right">{formattedCurrency(row.saldo_cc)}</TableCell>
        <TableCell align="right">{formattedCurrency(row.total_cr)}</TableCell>
        <TableCell align="right">{formattedCurrency(row.total_ci_sin_recibo)}</TableCell>

        {isAccounts && (
          <TableCell align="right">{formattedCurrency(row.saldo_cc_ctrl_date!)}</TableCell>
        )}

        {isAccounts && (
          <TableCell align="right">{formattedCurrency(row.total_cr_ctrl_date!)}</TableCell>
        )}
      </ISTableRow>
    ))
  const renderTotalRow = (
    label: string,
    subtotal: ReturnType<typeof calculateTotals>,
    key: string,
    sticky = false
  ) => (
    <ISTableRow
      key={key}
      color="default"
      sx={{
        ...(sticky ? { position: 'sticky', bottom: 0, zIndex: 2 } : {}),
        backgroundColor: theme.palette.info.main,
        fontWeight: 'bold'
      }}
    >
      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{label}</TableCell>
      <TableCell />
      <TableCell />
      <TableCell />

      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
        {formattedCurrency(subtotal.total_documentos_vencidos)}
      </TableCell>

      <TableCell />

      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
        {formattedCurrency(subtotal.total_rc_sin_aplicar)}
      </TableCell>

      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
        {formattedCurrency(subtotal.total_nc_sin_aplicar)}
      </TableCell>

      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
        {formattedCurrency(subtotal.saldo_cc)}
      </TableCell>

      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
        {formattedCurrency(subtotal.total_cr)}
      </TableCell>

      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
        {formattedCurrency(subtotal.total_ci_sin_recibo)}
      </TableCell>

      {isAccounts && (
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
          {formattedCurrency(subtotal.saldo_cc_ctrl_date)}
        </TableCell>
      )}

      {isAccounts && (
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
          {formattedCurrency(subtotal.total_cr_ctrl_date)}
        </TableCell>
      )}
    </ISTableRow>
  )

  return (
    <Stack
      sx={{
        //height: 'calc(89vh - var(--header-height))',
        flex: 1,
        borderRadius: '10px',
        margin: '10px',
        overflow: 'hidden'
      }}
    >
      {/* Título fijo */}
      <ISTableTitle title={t('summaryTitle')} />
      {loading && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 2, py: 1 }}
        >
          <CircularProgress size={18} />
          <Typography variant="body2">
            Cargando información...
          </Typography>
        </Stack>
      )}

      {/* Scroll SOLO acá */}
      <TableContainer sx={{ height: '100%', overflowY: 'auto', overflowX: isSmallScreen ? 'auto' : 'hidden' }}>
        <Table stickyHeader size="medium"
          sx={{
            //tableLayout: isSmallScreen ? 'auto' : 'fixed',
            minWidth: isSmallScreen ? 1000 : '100%'
          }}>
          <TableHead>
            <TableRow selected>

              {/* Zona */}
              <ISTableHeaderCell sortDirection={orderBy === 'zona_nombre' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'zona_nombre'}
                  direction={orderBy === 'zona_nombre' ? orderDirection : 'asc'}
                  onClick={() => handleSort('zona_nombre')}
                >
                  {t('zone', { ns: 'goal' })}
                </TableSortLabel>
              </ISTableHeaderCell>

              {/* Vendedor */}
              <ISTableHeaderCell sortDirection={orderBy === 'vendedor' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'vendedor'}
                  direction={orderBy === 'vendedor' ? orderDirection : 'asc'}
                  onClick={() => handleSort('vendedor')}
                >
                  {t('vendor', { ns: 'goal' })}
                </TableSortLabel>
              </ISTableHeaderCell>

              {/* Código Cliente */}
              <ISTableHeaderCell align="center" sortDirection={orderBy === 'codigo_cliente' ? orderDirection : false} sx={{ width: 90 }}>
                <TableSortLabel
                  active={orderBy === 'codigo_cliente'}
                  direction={orderBy === 'codigo_cliente' ? orderDirection : 'asc'}
                  onClick={() => handleSort('codigo_cliente')}
                >
                  {t('code', { ns: 'client' })}
                </TableSortLabel>
              </ISTableHeaderCell>

              {/* Cliente */}
              <ISTableHeaderCell sortDirection={orderBy === 'cliente' ? orderDirection : false} sx={{ width: 250 }}>
                <TableSortLabel
                  active={orderBy === 'cliente'}
                  direction={orderBy === 'cliente' ? orderDirection : 'asc'}
                  onClick={() => handleSort('cliente')}
                >
                  {t('name', { ns: 'client' })}
                </TableSortLabel>
              </ISTableHeaderCell>

              {/* Doc vencidos */}
              <ISTableHeaderCell align="right" sortDirection={orderBy === 'total_documentos_vencidos' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'total_documentos_vencidos'}
                  direction={orderBy === 'total_documentos_vencidos' ? orderDirection : 'asc'}
                  onClick={() => handleSort('total_documentos_vencidos')}
                >
                  {t('expiredDocs')}
                </TableSortLabel>
              </ISTableHeaderCell>

              {/* Vencimiento */}
              <ISTableHeaderCell align='center' sortDirection={orderBy === 'vencimiento_mas_atrasado' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'vencimiento_mas_atrasado'}
                  direction={orderBy === 'vencimiento_mas_atrasado' ? orderDirection : 'asc'}
                  onClick={() => handleSort('vencimiento_mas_atrasado')}
                >
                  {t('oldestDueDate')}
                </TableSortLabel>
              </ISTableHeaderCell>

              {/* RC */}
              <ISTableHeaderCell align="right" sortDirection={orderBy === 'total_rc_sin_aplicar' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'total_rc_sin_aplicar'}
                  direction={orderBy === 'total_rc_sin_aplicar' ? orderDirection : 'asc'}
                  onClick={() => handleSort('total_rc_sin_aplicar')}
                >
                  {t('totalRC')}
                </TableSortLabel>
              </ISTableHeaderCell>

              {/* NC */}
              <ISTableHeaderCell align="right" sortDirection={orderBy === 'total_nc_sin_aplicar' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'total_nc_sin_aplicar'}
                  direction={orderBy === 'total_nc_sin_aplicar' ? orderDirection : 'asc'}
                  onClick={() => handleSort('total_nc_sin_aplicar')}
                >
                  {t('totalNC')}
                </TableSortLabel>
              </ISTableHeaderCell>

              {/* Saldo */}
              <ISTableHeaderCell align="right" sortDirection={orderBy === 'saldo_cc' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'saldo_cc'}
                  direction={orderBy === 'saldo_cc' ? orderDirection : 'asc'}
                  onClick={() => handleSort('saldo_cc')}
                >
                  {t('balanceCA')}
                </TableSortLabel>
              </ISTableHeaderCell>

              {/* CR */}
              <ISTableHeaderCell align="right" sortDirection={orderBy === 'total_cr' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'total_cr'}
                  direction={orderBy === 'total_cr' ? orderDirection : 'asc'}
                  onClick={() => handleSort('total_cr')}
                >
                  {t('bouncedChecks')}
                </TableSortLabel>
              </ISTableHeaderCell>

              {/* CI */}
              <ISTableHeaderCell align="right" sortDirection={orderBy === 'total_ci_sin_recibo' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'total_ci_sin_recibo'}
                  direction={orderBy === 'total_ci_sin_recibo' ? orderDirection : 'asc'}
                  onClick={() => handleSort('total_ci_sin_recibo')}
                >
                  {t('electronicStock')}
                </TableSortLabel>
              </ISTableHeaderCell>

              {isAccounts && (
                <ISTableHeaderCell align="right" sortDirection={orderBy === 'saldo_cc_ctrl_date' ? orderDirection : false}>
                  <TableSortLabel
                    active={orderBy === 'saldo_cc_ctrl_date'}
                    direction={orderBy === 'saldo_cc_ctrl_date' ? orderDirection : 'asc'}
                    onClick={() => handleSort('saldo_cc_ctrl_date')}
                  >
                    {t('balanceCADate')}
                  </TableSortLabel>
                </ISTableHeaderCell>
              )}
              {isAccounts && (
                <ISTableHeaderCell align="right" sortDirection={orderBy === 'total_cr_ctrl_date' ? orderDirection : false}>
                  <TableSortLabel
                    active={orderBy === 'total_cr_ctrl_date'}
                    direction={orderBy === 'total_cr_ctrl_date' ? orderDirection : 'asc'}
                    onClick={() => handleSort('total_cr_ctrl_date')}
                  >
                    {t('bouncedChecksDate')}
                  </TableSortLabel>
                </ISTableHeaderCell>
              )}

            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && nationalRows.length > 0 && (
              <>
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      backgroundColor: alpha(theme.palette.primary.main, 0.12)
                    }}
                  >
                    MERCADO NACIONAL
                  </TableCell>
                </TableRow>

                {renderRows(nationalRows)}

                {renderTotalRow(
                  'TOTAL MERCADO NACIONAL',
                  nationalTotals,
                  'total-national'
                )}
              </>
            )}

            {!loading && exportRows.length > 0 && (
              <>
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      backgroundColor: alpha(theme.palette.primary.main, 0.12)
                    }}
                  >
                    EXPORTACIÓN
                  </TableCell>
                </TableRow>

                {renderRows(exportRows)}

                {renderTotalRow(
                  'TOTAL EXPORTACIÓN',
                  exportTotals,
                  'total-export'
                )}
              </>
            )}

            {!loading && rows.length > 0 &&
              renderTotalRow(
                'TOTAL GENERAL',
                totals,
                'total-general',
                true
              )
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default CurrentAccountSummaryTable
